/**
 * This file helps put together the AI queries for functionality available in the AI assistant.
 * (AI Chat interface in the sidebar)
 */

import { addSeconds } from "date-fns";
import { isEqual } from "lodash-es";

import { createId } from "../../../shared/utils";
import { DEFAULT_MODEL_FOR_CHAT } from "../components/AISettings";
import { getSelectedTools } from "../tools";
import type { ToolNameType } from "../tools/types";

import { serverGetAIEventStreamResponse } from "api/api-server-ai";
import {
  AIServerFileType,
  AIServerMessageType,
  SSEToolCallDataType,
} from "components/server-types";
import { t } from "i18n/i18n";
import { AppDispatch, store } from "store/index";
import { selectIsStudentResponsesForAIEnabled } from "store/selectors";
import {
  selectAiChatMessages,
  selectContextFiles,
  selectContextMessages,
  selectToolNames,
  selectUserSelectedAIModel,
} from "store/selectors/ai";
import {
  AIChatMessage,
  AIContextItem,
  AITextMessage,
  AIToolMessage,
  AIToolResponseMessage,
  aiStateActions,
} from "store/slices/ai";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { rollbarAndLogError } from "utils/logger";

export const DEFAULT_AI_INTRO_MESSAGE: AIChatMessage = {
  type: "message",
  id: "intro",
  text: t("ai.intro_message"),
  createdAt: new Date().toISOString(),
  role: "assistant",
  isLoading: false,
};

// TODO: This is not currently sent to the AI or covered in the snapshot test
export const getDefaultMessage = (isStudent: boolean, isStudentResponsesForAIEnabled: boolean) => {
  if (isStudentResponsesForAIEnabled) {
    return DEFAULT_AI_INTRO_MESSAGE;
  }

  // If we don't have student responses for AI enabled, we append a message to the default intro message
  // to inform users about the option.
  const enableMessage: string = isStudent
    ? t("ai.student_ask_for_page_permissions")
    : t("ai.instructor_ask_for_page_permissions");

  return {
    ...DEFAULT_AI_INTRO_MESSAGE,
    text: `${DEFAULT_AI_INTRO_MESSAGE.text} ${enableMessage}`,
  };
};

export const AI_SUMMARIZE_RESPONSES_MESSAGE =
  "Analyze these student responses and summarize briefly common correct ideas and identify common misconceptions or errors";

/**
 * This file is responsible for taking various contexts and user or system messages
 * to construct a list of messages corresponding to the OpenAI API.
 *
 * An example output would be:
 * [
 *  { role: "system", text: "<generic context for AI>, <context and data for current page>" },
 *  { role: "assistant", text: "<introduction message hardcoded on client>" },
 *  { role: "user", text: "<first user request>" },
 *  { role: "assistant", text: "<response from AI>" },
 *  { role: "user", text: "<second user request>" },
 *  etc.
 * ]
 *
 */
export const sendAIChatQuery = async (
  dispatch: AppDispatch,
  sectionId: string | null,
  userInput: string,
  messageFiles?: AIServerFileType[],
) => {
  const state = store.getState();
  const shareStudentResponse = selectIsStudentResponsesForAIEnabled(state);
  const model = selectUserSelectedAIModel(state) || DEFAULT_MODEL_FOR_CHAT;
  const contextMessages = selectContextMessages(state);
  const existingMessages = selectAiChatMessages(state);
  const contextFiles = selectContextFiles(state);
  const toolNames = selectToolNames(state);
  const tools = getSelectedTools(toolNames);

  const allFiles = [...contextFiles, ...(messageFiles || [])];

  // Construct a message object representing the user's input, and if the input isn't blank,
  // insert it into the chat history and later include it in the query sent to the server.
  const timestamp = new Date();
  const userInputMessage: AIChatMessage = {
    id: createId(),
    type: "message",
    role: "user",
    text: userInput,
    createdAt: timestamp.toISOString(),
    files: messageFiles,
  };
  const shouldSendUserMessage =
    userInputMessage.text.trim() !== "" || (messageFiles?.length ?? 0) > 0;
  if (shouldSendUserMessage) {
    dispatch(aiStateActions.setAiChatMessage(userInputMessage));
  }

  // Construct a placeholder message object representing the server's response, which will be
  // populated later when the response is received, or else deleted in the case of a response
  // that contains tool calls. Set `isLoading` to true initially so that the message will be
  // rendered with an animated ellipsis; this will be cleared later when we start to receive
  // a response. Set `createdAt` to one second after the user's input message to make sure the
  // response appears after the input message in the chat window.
  const serverResponseMessage: AITextMessage = {
    id: createId(),
    type: "message",
    role: "assistant",
    text: "",
    createdAt: addSeconds(timestamp, 1).toISOString(),
    isLoading: true,
  };
  dispatch(aiStateActions.setAiChatMessage(serverResponseMessage));

  try {
    // Assemble a combined list of chat messages and context items to be sent
    // along with the query to the server.
    const contextItems: (AIChatMessage | AIContextItem)[] = [
      {
        type: "context",
        role: "system",
        text: "Right now, you're engaging directly with the user via chat.",
      } as AIContextItem,
      ...contextMessages,
      ...existingMessages.filter((msg) => msg.type !== "message" || !msg.isLoading),
      ...(shouldSendUserMessage ? [userInputMessage] : []),
    ];

    // Convert the combined list of chat messages and context items into the
    // format expected by the server.
    const prompt: AIServerMessageType[] = contextItems.map((item) => {
      switch (item.type) {
        case "context":
        case "message":
          return {
            role: item.role,
            content: item.text,
            id: item.id,
          };
        case "function_call":
          return {
            type: "function_call",
            call_id: item.call_id,
            name: item.name,
            arguments: item.arguments,
            id: item.id,
          };
        case "function_call_output":
          return {
            type: "function_call_output",
            call_id: item.call_id,
            output:
              typeof item.output === "string" ? { data: item.output } : JSON.stringify(item.output),
            id: item.id,
          };
        default:
          throw new Error("Invalid context item type for chat query");
      }
    });

    // Track some information about the request.
    trackEvent("AI Assistant - Send message", {
      ...BUTTON_EVENT_CATEGORY,
      messageLength: userInput.length,
      message: userInput,
      messageNumber: existingMessages.length + 1,
      pageUrl: window.location.href,
      shareStudentResponse,
    });

    // Create the callback function we'll pass to serverGetAIEventStreamResponse that it
    // will use to handle each chunk of response data as it's received from the server.
    const onTextDelta = (delta: string) => {
      if (!delta) return;
      serverResponseMessage.text += delta;
      serverResponseMessage.isLoading = false;
      dispatch(aiStateActions.setAiChatMessage(serverResponseMessage));
    };

    // Create the callback function we'll pass to serverGetAIEventStreamResponse that it
    // will use to handle any tool calls it may receive from the server.
    const onToolCalls = async (calls: SSEToolCallDataType[]) => {
      // Cancel the pending server response message we created above.
      dispatch(aiStateActions.deleteAiChatMessage(serverResponseMessage.id));

      // For each tool call we receive from the server, invoke the tool to obtain a result,
      // and insert both the call and the result into the chat history as message objects.
      const promises = calls.map(async ({ name, arguments: args }) => {
        // The tool call we received from the server gets encoded into a message object
        // in the chat history so it will be included in future queries. This gives the
        // LLM a way to correlate its tool call with our response to it, and will
        // hopefully help it to avoid issuing duplicate calls.
        const tooCallMessage: AIToolMessage = {
          id: createId(),
          type: "function_call",
          name: name as ToolNameType,
          arguments: args,
          call_id: createId(),
          createdAt: new Date().toISOString(),
        } as AIToolMessage;
        dispatch(aiStateActions.setAiChatMessage(tooCallMessage));

        // Construct a placeholder message object that will contain our response to the tool call.
        const toolResponseMessage: AIToolResponseMessage = {
          id: `function-output-${tooCallMessage.call_id}`,
          type: "function_call_output",
          call_id: tooCallMessage.call_id,
          output: {},
          createdAt: new Date().toISOString(),
          role: "assistant",
        };

        // Detect duplicate tool calls and deal with them by responding with an error message
        // that the LLM will hopefully understand and react to in order to fix its behavior.
        const duplicateCalls = existingMessages.filter(
          (msg) =>
            msg.type === "function_call" &&
            msg.name === tooCallMessage.name &&
            isEqual(msg.arguments, tooCallMessage.arguments),
        );
        if (duplicateCalls.length > 1) {
          const errorMessage = "Duplicate tool call";
          toolResponseMessage.error = errorMessage;
          toolResponseMessage.output = { error: errorMessage };
          dispatch(aiStateActions.setAiChatMessage(toolResponseMessage));
          return;
        }

        // Find matching tool.
        const tool = tools.find((toolDef) => toolDef.name === tooCallMessage.name);
        if (
          !tool ||
          typeof tool.callback !== "function" ||
          !["function", "string"].includes(typeof tool.progressMessage)
        ) {
          const errorMessage = "Tool not found";
          toolResponseMessage.error = errorMessage;
          toolResponseMessage.output = { error: errorMessage };
          dispatch(aiStateActions.setAiChatMessage(toolResponseMessage));
          return;
        }

        // Render a progress message for the tool call.
        if (typeof tool.progressMessage === "function") {
          tooCallMessage.status = tool.progressMessage(tooCallMessage.arguments);
        } else {
          tooCallMessage.status = tool.progressMessage;
        }
        dispatch(aiStateActions.setAiChatMessage(tooCallMessage));

        // Invoke the tool to generate a result, then insert the result into the chat history.
        try {
          toolResponseMessage.output = await tool.callback(tooCallMessage.arguments);
        } catch (err) {
          rollbarAndLogError("Error in tool call", tooCallMessage, err);
          toolResponseMessage.error = String(err);
        }
        toolResponseMessage.finishedAt = new Date().toISOString();
        dispatch(aiStateActions.setAiChatMessage(toolResponseMessage));
      });

      // Wait for all those tool call responses to be generated.
      await Promise.all(promises);

      // Re-send the chat query without content for follow-up.
      sendAIChatQuery(dispatch, sectionId, "");
    };

    // Make a request to the server passing the combined and converted prompt, attached
    // files if any, and enabled tools if any. Pass `onTextDelta` and `onToolCalls` as
    // callback functions for it to call when it receives a response from the server.
    await serverGetAIEventStreamResponse({
      section_id: sectionId,
      model,
      prompt,
      files: allFiles,
      tools,
      onTextDelta,
      onToolCalls,
    });

    // TODO: do we want to track the specific details of a tool call response?
    trackEvent("AI Assistant - Response received", {
      eventCategory: "General",
      message: serverResponseMessage.text || "tool calls",
    });
  } catch (e) {
    rollbarAndLogError("Error in sendAIChatQuery", e);
    serverResponseMessage.text = t("error.toasts.get_ai_response_failure");
    dispatch(aiStateActions.setAiChatMessage(serverResponseMessage));
    trackEvent("AI Assistant - Response failed", {
      eventCategory: "General",
      message: serverResponseMessage.text,
    });
  }
};
